import {clamp} from 'lodash';

import {type StepContext} from '../layout/types';
import {type StepMeta, type StepMetaRun} from '../types';

/**
 * Strategy for resolving collisions when multiple steps map to the same slider value
 */
export type SliderStepCollisionStrategy = 'latest' | 'earliest' | 'custom';

/**
 * Mapping between a step value and its corresponding slider step value
 */
export interface SliderStepMapping {
  step: number;
  sliderStep: number;
}

/**
 * Result of resolving slider step collisions
 */
export interface SliderStepCollisionResult {
  uniqueSliderSteps: number[];
  stepsBySliderStep: Map<number, number>;
  resolvedSteps: number[];
}

/**
 * Resolves collisions when multiple steps map to the same slider value.
 * @param mappings Array of step to slider step mappings
 * @param strategy Strategy to use for resolving collisions
 * @param customResolver Optional custom resolver function
 * @returns Object containing the unique slider steps and their corresponding step values
 * @throws Error if using 'custom' strategy without providing a customResolver
 */
export function resolveSliderStepCollisions(
  mappings: SliderStepMapping[],
  strategy: SliderStepCollisionStrategy = 'latest',
  customResolver?: (steps: number[]) => number
): SliderStepCollisionResult {
  // Group steps by slider step
  const stepsBySliderStep = new Map<number, number[]>();
  mappings.forEach(({step, sliderStep}) => {
    if (!stepsBySliderStep.has(sliderStep)) {
      stepsBySliderStep.set(sliderStep, []);
    }
    stepsBySliderStep.get(sliderStep)!.push(step);
  });

  // Resolve collisions based on strategy
  const resolvedStepsBySliderStep = new Map<number, number>();
  const uniqueSliderSteps: number[] = [];
  const resolvedSteps: number[] = [];

  // Process slider steps in order
  Array.from(stepsBySliderStep.keys()).forEach(sliderStep => {
    const steps = stepsBySliderStep.get(sliderStep)!;
    let resolvedStep: number;

    switch (strategy) {
      case 'latest':
        resolvedStep = Math.max(...steps);
        break;
      case 'earliest':
        resolvedStep = Math.min(...steps);
        break;
      case 'custom':
        if (!customResolver) {
          throw new Error('Custom resolver required for custom strategy');
        }
        resolvedStep = customResolver(steps);
        break;
      default:
        resolvedStep = Math.max(...steps); // Default to latest
    }

    uniqueSliderSteps.push(sliderStep);
    resolvedSteps.push(resolvedStep);
    resolvedStepsBySliderStep.set(sliderStep, resolvedStep);
  });

  return {
    uniqueSliderSteps,
    stepsBySliderStep: resolvedStepsBySliderStep,
    resolvedSteps,
  };
}

/**
 * Finds the closest step that is less than or equal to the target value.
 *
 * This function ensures we always return a step that is <= target, which is important
 * for media fetching logic where we want to find the closest available step that
 * doesn't exceed the requested value.
 *
 * @param min - Minimum step value
 * @param max - Maximum step value
 * @param steps - Array of available step values (should be sorted)
 * @param target - Target value to find closest step for
 * @returns The closest step that is <= target, or min if no such step exists
 */
export function getClosestLowerStep(
  min: number,
  max: number,
  steps: number[],
  target: number
): number {
  if (target <= min) {
    return min;
  }
  if (target >= max) {
    return max;
  }

  // Handle empty steps array
  if (steps.length === 0) {
    return max;
  }

  // Filter to only steps <= target, then find the closest
  const lowerOrEqualSteps = steps.filter(step => step <= target);

  if (lowerOrEqualSteps.length === 0) {
    // No steps <= target, return min as fallback
    return min;
  }

  // Find the closest step among those <= target
  return lowerOrEqualSteps.reduce((prev, curr) =>
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  );
}

/**
 * Advanced tick snapping function that considers direction and uses binary search.
 * This is used for slider interactions where we want to snap to the nearest valid step
 * in the direction the user is moving.
 *
 * @param val Current value to snap
 * @param prev Previous value (used to determine direction)
 * @param min Minimum allowed value
 * @param max Maximum allowed value
 * @param ticks Array of valid tick values (should be sorted)
 * @returns The closest valid tick value in the appropriate direction
 */
export function getClosestTickWithDirection(
  val: number,
  prev: number,
  min: number,
  max: number,
  ticks?: number[]
): number {
  // if min/max not in ticks, allow coercion to nearest value
  if (val > max) {
    return max;
  }
  if (val < min) {
    return min;
  }
  if (ticks === null || ticks === undefined) {
    return val;
  }

  let closest = val;
  const increasing = val > prev;
  let minDiff = Number.MAX_VALUE;

  // Binary search for the closest tick
  let left = 0;
  let right = ticks.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const tick = ticks[mid];
    const diff = Math.abs(tick - val);

    // Only update closest if the tick is in the right direction
    if (
      diff < minDiff &&
      ((increasing && tick >= val) || (!increasing && tick <= val))
    ) {
      closest = tick;
      if (closest === val) {
        break;
      }
      minDiff = diff;
    }

    if (tick < val) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  return closest;
}

/**
 * This function is used to get the closest corresponding step and slider values for a run.
 *
 * NOTE: This will ALWAYS return something within the bounds of runStepMeta.
 * Our fething logic will look for available files logged before the requested step.
 * If the minimum value for the run is greater than the requested step,
 * we would expect to NOT find any files, and return the missing data cell.
 *
 * In order to avoid breaking the existing behavior, we implement that check when building the tiles.
 * @see getLastAvailableFilePathForTileMediaSpec
 *
 * @param runStepMeta
 * @param mediaSliderKey
 * @param sliderStep
 * @returns
 */
export function getStepFromSliderStep(
  runStepMeta: StepMetaRun,
  mediaSliderKey: string | undefined,
  sliderStep: number
): StepContext {
  const sliderKey = mediaSliderKey ?? '_step';
  const isDefaultSlider = sliderKey === '_step';

  if (sliderStep == null) {
    const maxValue = runStepMeta.maxSliderStep;
    const step = runStepMeta.sliderStepMap?.get(maxValue);
    return {
      inputStep: sliderStep,
      step: step ?? runStepMeta.max,
      sliderValue: maxValue,
      sliderKey,
    };
  }

  const min = isDefaultSlider ? runStepMeta.min : runStepMeta.minSliderStep;
  const max = isDefaultSlider ? runStepMeta.max : runStepMeta.maxSliderStep;
  const steps = isDefaultSlider ? runStepMeta.steps : runStepMeta.sliderSteps;

  const matchedStep = runStepMeta.sliderStepMap?.get(sliderStep);
  if (matchedStep !== undefined) {
    return {
      inputStep: sliderStep,
      step: matchedStep,
      sliderValue: sliderStep,
      sliderKey,
    };
  }
  const closestSliderStep = getClosestLowerStep(min, max, steps, sliderStep);
  const closestStep = runStepMeta.sliderStepMap?.get(closestSliderStep);
  return {
    inputStep: sliderStep,
    step: closestStep ?? sliderStep,
    sliderValue: closestSliderStep,
    sliderKey,
  };
}

/**
 * Determines the step value to use for media display based on snapping behavior and run bounds.
 *
 * This function implements the logic for deciding whether to use the snapped step value
 * (from getStepFromSliderStep) or the original step value. The decision is based on:
 * 1. Whether snapToExistingStep is enabled (always use snapped step)
 * 2. Whether the run has data at or before the requested step (use snapped step)
 *
 * Note: This is a workaround for the snapToExistingStep setting being used for both
 * slider behavior and media display logic. Ideally, these should be separate settings.
 *
 * @param runStepMeta - Run step metadata containing min/max values
 * @param sliderKey - The slider key being used ('_step' or custom key)
 * @param currentStep - The current step value (slider step value)
 * @param snapToExistingStep - Whether to snap to existing steps
 * @returns The step value to use for media display and the correspondingslider step value
 */
export function getStepToShow(
  runStepMeta: StepMetaRun,
  sliderKey: string,
  currentStep: number,
  snapToExistingStep: boolean | undefined
): {step: number; sliderStepValue?: number} {
  const stepContext = getStepFromSliderStep(
    runStepMeta,
    sliderKey,
    currentStep
  );

  // This mimics our fetching logic where we resolve the file from
  // the closest step less than or equal to the requested.
  // If the minimum value for the run is greater than the requested step,
  // we treat this as though the run does have data for the requested step.
  const minimumStep =
    sliderKey === '_step' ? runStepMeta.min : runStepMeta.minSliderStep;
  const shouldShowMediaForRun = minimumStep <= currentStep;
  // We have applied a dual meaning to this setting:
  // 1. Skip to the next slider value when incrementing the step
  // 2. Resolve the closest actual value for the current slider value
  const shouldShowClosestStep = snapToExistingStep ?? false;
  // In the future we will split these into separate settings,
  // but for now we treat these interchangeably for backwards compatibility.
  const shouldMapStep = shouldShowClosestStep || shouldShowMediaForRun;

  return {
    step: shouldMapStep ? stepContext.step : currentStep,
    sliderStepValue: shouldMapStep ? stepContext.sliderValue : currentStep,
  };
}

/**
 * Gets the default step value to use when no explicit step is provided.
 *
 * This function determines the appropriate default step based on the slider key:
 * - For '_step': returns stepMeta.max (the maximum _step value)
 * - For custom slider keys: returns stepMeta.maxSliderStep (the maximum slider step value)
 *
 * @param stepMeta - Step metadata containing max and maxSliderStep values
 * @param sliderKey - The slider key being used ('_step' or custom key)
 * @returns The default step value to use
 */
export function getDefaultStep(stepMeta: StepMeta, sliderKey: string): number {
  return sliderKey === '_step' ? stepMeta.max : stepMeta.maxSliderStep;
}

/**
 * Resolves the current step value from config, falling back to the default step.
 *
 * This is a convenience function that combines explicit step value resolution
 * with default step calculation. It's commonly used throughout the codebase to
 * determine which step value to use for display or media fetching.
 *
 * @param stepFromConfig - Explicit step value from config (may be undefined)
 * @param stepMeta - Step metadata containing max and maxSliderStep values
 * @param sliderKey - The slider key being used ('_step' or custom key)
 * @returns The resolved step value (explicit if provided, otherwise default)
 */
export function resolveCurrentStep(
  stepFromConfig: number | undefined,
  stepMeta: StepMeta,
  sliderKey: string | undefined
): number {
  return stepFromConfig ?? getDefaultStep(stepMeta, sliderKey ?? '_step');
}

export function calculateStepBounds(steps: number[]) {
  return steps.reduce(
    (acc, step) => {
      acc.localResolveMin = Math.min(acc.localResolveMin, step);
      acc.localResolvedMax = Math.max(acc.localResolvedMax, step);
      return acc;
    },
    {localResolveMin: Infinity, localResolvedMax: -Infinity}
  );
}

/**
 * Resolves the slider key to use based on priority order.
 *
 * Priority order (highest to lowest):
 * 1. runStepMeta.sliderKey (if provided and not '_step')
 * 2. mediaSliderKey (if provided and not '_step')
 * 3. '_step' (fallback)
 *
 * @param runStepMeta - Optional run step metadata with sliderKey
 * @param mediaSliderKey - Media slider key from config
 * @returns The resolved slider key to use
 */
export function resolveSliderKey(
  runStepMeta?: {sliderKey?: string},
  mediaSliderKey?: string
): string {
  if (runStepMeta?.sliderKey && runStepMeta.sliderKey !== '_step') {
    return runStepMeta.sliderKey;
  }
  if (mediaSliderKey && mediaSliderKey !== '_step') {
    return mediaSliderKey;
  }
  return '_step';
}

/**
 * Updates a slider value with stride validation and snapping to existing ticks.
 *
 * NOTE: This function validates stride-based movement. Consider using `changeSliderValue`
 * for simpler delta-based updates without stride validation.
 *
 * @param newVal The new value to set
 * @param current The current value
 * @param ticks Array of valid tick values
 * @param strideLength The stride length for validation
 */
export function getIndexFromSliderValue(
  newVal: number,
  current: number,
  ticks: number[],
  strideLength: number
): number | undefined {
  const currentIndex = ticks.indexOf(current);
  // If no change, return current index
  if (newVal === current) {
    return currentIndex;
  }

  // If current value is in ticks, try stride-based movement first
  if (currentIndex !== -1) {
    const delta = newVal - current;
    if (delta % strideLength === 0) {
      const steps = delta / strideLength;
      const newIndex = currentIndex + steps;
      return Math.max(0, Math.min(newIndex, ticks.length - 1));
    }
  }

  // Fallback: try to find newVal directly in ticks
  const directIndex = ticks.indexOf(newVal);
  if (directIndex !== -1) {
    return directIndex;
  }

  return undefined;
}

/**
 * Increments or decrements a slider value
 *
 * @param currentValue - Current slider value
 * @param delta - Amount to change by (positive for increment, negative for decrement)
 * @param options - Configuration options
 * @returns New value after applying delta
 */
export function changeSliderValue(
  currentValue: number,
  delta: number,
  options: {
    min: number;
    max: number;
    availableValues?: number[];
    snapToAvailableValues?: boolean;
    stride?: number;
  }
): number {
  const {
    min,
    max,
    availableValues = [],
    snapToAvailableValues = false,
    stride = 1,
  } = options;

  // Snap to available values mode
  if (snapToAvailableValues && availableValues.length > 0) {
    const currentIdx = availableValues.indexOf(currentValue);

    // If current value is in the array, just increment/decrement index
    if (currentIdx !== -1) {
      const newIdx = clamp(currentIdx + delta, 0, availableValues.length - 1);
      return availableValues[newIdx];
    }

    // Current value not in array - find closest and move from there
    const closest = availableValues.reduce((prev, curr) =>
      Math.abs(curr - currentValue) < Math.abs(prev - currentValue)
        ? curr
        : prev
    );
    const closestIdx = availableValues.indexOf(closest);
    const newIdx = clamp(closestIdx + delta, 0, availableValues.length - 1);
    return availableValues[newIdx];
  }

  // Continuous mode - apply stride
  const newValue = currentValue + delta * stride;
  return clamp(newValue, min, max);
}

/**
 * Prefer slider steps when non-empty; otherwise use plain steps.
 * Used for aggregated step metadata (gallery/grid), per-run metadata, and
 * compare-mode column fallbacks.
 */
export function pickSliderStepsOrSteps(
  sliderSteps: number[],
  steps: number[]
): number[] | undefined {
  if (sliderSteps.length > 0) {
    return sliderSteps;
  }
  if (steps.length > 0) {
    return steps;
  }
  return undefined;
}

/** Indices list for controls when the list is non-empty. */
export function pickNonEmptyIndices(indices: number[]): number[] | undefined {
  return indices.length > 0 ? indices : undefined;
}

/** Global step lists from aggregated step metadata (gallery / grid). */
export function getAggregatedAvailableSteps(
  stepMeta: StepMeta | undefined
): number[] | undefined {
  if (!stepMeta) {
    return undefined;
  }
  return pickSliderStepsOrSteps(stepMeta.sliderSteps, stepMeta.steps);
}

/** Global index lists from aggregated step metadata (gallery / grid). */
export function getAggregatedAvailableIndices(
  stepMeta: StepMeta | undefined
): number[] | undefined {
  if (!stepMeta) {
    return undefined;
  }
  return pickNonEmptyIndices(stepMeta.indices);
}
